import { X } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';

/**
 * One 44px control at the right-hand end of the rail, and the panel it opens. Phone only.
 *
 * Below `md` the header carries the brand block and very little else, so the two things that used
 * to live in that row — the section nav, and the room/role/account cluster — each collapse behind
 * one of these. Extracted rather than written twice because what has to stay identical between
 * them is not the styling but the **layering**: `Modal` and `AboutDialog` are each `z-50`, so a
 * panel at `z-40` over a backdrop at `z-30` is what puts the unsaved-work prompt and the About
 * dialog *over* whichever panel raised them. Two copies of that would agree until one was
 * adjusted.
 *
 * `children` is a render prop taking `close`, because every row that acts has to dismiss the panel
 * itself — and closing before the handler runs is load-bearing rather than tidy: `leave` can raise
 * the unsaved-work prompt, and that question should be read over the screen rather than through a
 * panel.
 *
 * Chalk, never signal orange. Neither panel carries work waiting on the operator — one says where
 * you are, the other says who you are — and orange in the gallery means "you must act".
 */
export function RailPanel({
  icon,
  label,
  heading,
  children,
  hideFrom,
}: {
  /** The breakpoint class that hides the control: from `desktop:` (the phone's arrangement) unless a caller says. */
  hideFrom?: string;
  /** The glyph in the rail control. */
  icon: ReactNode;
  /** Names the control and the panel to a screen reader. */
  label: string;
  /** The panel's own top row, beside the close button. */
  heading: ReactNode;
  children: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const close = () => setOpen(false);

  // Focus lands on the panel itself rather than on the first control: on iOS focusing a `select`
  // can raise its picker, so autofocusing one would open the panel *and* a wheel over it.
  useEffect(() => {
    if (open) panelRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <div className={hideFrom ?? 'desktop:hidden'}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={label}
        className="flex size-11 items-center justify-center border border-white/20 text-rail-300 transition hover:border-white/50 hover:text-white"
      >
        {icon}
      </button>

      {open ? (
        <>
          <div className="fixed inset-0 z-30 bg-black/50" onClick={close} aria-hidden />
          <div
            ref={panelRef}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-label={label}
            // `top-14` is the header's own `h-14`, so the panel hangs flush off the bottom of the
            // rail. `max-h` and the inner scroll keep a long list inside the viewport rather than
            // under the foot of a shell that does not scroll.
            className="fixed right-2 top-14 z-40 flex max-h-[calc(100dvh-4.5rem)] w-[min(19rem,calc(100vw-1rem))] flex-col border border-white/15 bg-rail-900 shadow-2xl outline-none"
          >
            <div className="flex shrink-0 items-start justify-between gap-3 px-4 pb-3 pt-3.5">
              <div className="min-w-0">{heading}</div>
              <button
                type="button"
                onClick={close}
                aria-label="Close"
                className="-mr-1 -mt-1 flex size-8 shrink-0 items-center justify-center text-rail-300 transition hover:text-white"
              >
                <X className="size-4" aria-hidden />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">{children(close)}</div>
          </div>
        </>
      ) : null}
    </div>
  );
}

/** The caps label above a group inside a panel, and on the panel's own heading row. */
export const panelLabel = 'text-[10px] font-bold uppercase tracking-[0.18em] text-rail-300';
